import { HttpErrorResponse } from '@angular/common/http';
import {
    IMultiYearAvailableLanguage,
    IMultiYearComparisonResponseDTO,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IMycCompanyPreferences } from '@modeso/types__aumico-lib-companies-be';
import { createAction, props } from '@ngrx/store';
import { DeltaMode, ReportScope } from '../components/multi-year-comparison/models/controls.model';

// Load
export const loadAnalyticsForCompany = createAction(
    '[Analytics] Load',
    props<{ companyId: string }>(),
);
export const loadAnalyticsSuccess = createAction(
    '[Analytics] Load success',
    props<{ companyId: string; harmonized: IMultiYearComparisonResponseDTO }>(),
);
export const loadAnalyticsFailure = createAction(
    '[Analytics] Load failure',
    props<{ companyId: string; error: HttpErrorResponse }>(),
);

// Language mismatch flow — BE returns HTTP 409 when reports span multiple
// locales without `?language=`. The FE captures the available languages, opens
// a forced-choice modal (`disableClose: true`, no Cancel button), and the
// only exit from that modal re-dispatches `setMultiYearLanguage` with the
// user's pick. `setMultiYearLanguage` also doubles as the permanent-switch
// trigger (controls-bar dropdown) so the user can flip language anytime.
export const requireLanguageSelection = createAction(
    '[Analytics] Require language selection',
    props<{ companyId: string; availableLanguages: IMultiYearAvailableLanguage[] }>(),
);
export const setMultiYearLanguage = createAction(
    '[Analytics] Set multi-year language',
    props<{ companyId: string; language: string }>(),
);

// Controls
export const toggleYear = createAction(
    '[Analytics] Toggle year',
    props<{ companyId: string; yearLabel: string }>(),
);
export const setDeltaMode = createAction(
    '[Analytics] Set delta mode',
    props<{ companyId: string; mode: DeltaMode }>(),
);
export const setReportScope = createAction(
    '[Analytics] Set report scope',
    props<{ companyId: string; scope: ReportScope }>(),
);
export const toggleStructMode = createAction(
    '[Analytics] Toggle struct mode',
    props<{ companyId: string }>(),
);
export const toggleNode = createAction(
    '[Analytics] Toggle node',
    props<{ companyId: string; nodeId: string }>(),
);
export const expandAllNodes = createAction(
    '[Analytics] Expand all nodes',
    props<{ companyId: string }>(),
);
export const collapseAllNodes = createAction(
    '[Analytics] Collapse all nodes',
    props<{ companyId: string }>(),
);

// PDF export (Mehrjahresvergleich) — V2 async via Pub/Sub + WebSocket
//
// `selectedNodeIds`: ids picked in the export modal (Group / FLI / SubRow /
// SubTotal). Strict per-row filter; no descendant cascade.
// `showAccounts`:    when true, the mapper emits `Acc` rows for every account
// under each visible FLI / SUB_FLI. Toggled globally in the export modal.
// `includeBalance` / `includeIncome`: section gates from the "Bestandteile"
// accordion. When OFF the mapper emits an empty `data` array for that section
// and the .docx template skips it entirely.
// `showRatios`: modal override for the Verhältniskennzahl column. When
// `undefined`, the mapper falls back to the in-app `controls.structMode`.
export const requestMultiYearPdf = createAction(
    '[Analytics] Request multi-year PDF',
    props<{
        companyId: string;
        selectedNodeIds?: string[];
        showAccounts?: boolean;
        includeBalance?: boolean;
        includeIncome?: boolean;
        showRatios?: boolean;
    }>(),
);
export const requestMultiYearPdfSuccess = createAction(
    '[Analytics] Request multi-year PDF success',
    props<{ companyId: string }>(),
);
export const requestMultiYearPdfFailure = createAction(
    '[Analytics] Request multi-year PDF failure',
    props<{ companyId: string; error: unknown }>(),
);

// ─── Persisted Mehrjahresvergleich preferences (company-scoped) ─────────────
//
// Write strategy is dirty-flag + periodic / page-leave flush rather than a
// PATCH per UI change — keeps the BE rate limiter happy when the user clicks
// rapidly. Reducer cases for `toggleYear / setDeltaMode / toggleStructMode /
// setMultiYearLanguage` flip a `mycPreferencesDirty` flag (no extra action
// needed); the flush is then triggered by one of three things:
//   1. `periodicFlush$` effect: timer every 30s, dispatches `flushMycPreferences`
//      for every company whose entry is dirty.
//   2. Container `ngOnDestroy` and the `companyId` switch in `ngOnChanges` —
//      both dispatch `flushMycPreferences` explicitly.
//   3. Export-Modal Confirm: separate `persistExportModalConfirm` action,
//      flushed immediately (no dirty-flag involvement).
//
// `hydrateMycPreferences`: dispatched after the company GET completes; merges
// the persisted prefs into the analytics entry and clears the dirty flag
// (server-state IS the truth at hydration time).
export const hydrateMycPreferences = createAction(
    '[Analytics] Hydrate MYC preferences',
    props<{ companyId: string; preferences: IMycCompanyPreferences }>(),
);
/**
 * Explicit "write the current dirty state now" trigger. No-op when the
 * entry is clean (no changes since last flush / hydration).
 */
export const flushMycPreferences = createAction(
    '[Analytics] Flush MYC preferences',
    props<{ companyId: string }>(),
);
/** Confirmation from the flush effect — clears `mycPreferencesDirty`. */
export const mycPreferencesFlushed = createAction(
    '[Analytics] MYC preferences flushed',
    props<{ companyId: string }>(),
);
export const persistExportModalConfirm = createAction(
    '[Analytics] Persist export-modal preferences (on confirm)',
    props<{
        companyId: string;
        includeBalance: boolean;
        includeIncome: boolean;
        showAccounts: boolean;
        showRatios: boolean;
        selectedNodeIds: string[];
    }>(),
);
